/**
 * Thin wrapper around getUserMedia that turns the various failure modes
 * into a discriminated result instead of throwing different DOMException
 * subclasses. Lets RoomClient and the Room page treat "denied" and
 * "no devices" uniformly (US2 / FR-013).
 */

export type MediaResult =
  | { ok: true; stream: MediaStream }
  | { ok: false; reason: MediaFailure }

export type MediaFailure =
  | 'denied' // user clicked Block (NotAllowedError)
  | 'no-device' // no camera/mic attached (NotFoundError / OverconstrainedError)
  | 'unsupported' // mediaDevices API missing (insecure context, very old browser)
  | 'unknown'

export interface MediaConstraints {
  video: boolean | MediaTrackConstraints
  audio: boolean
}

/**
 * Parse a positive integer from a Vite env var, falling back when unset or
 * malformed. Used to make the capture target tunable (e.g. the Playwright
 * suite runs a light profile so parallel fake-device encodes don't saturate
 * CPU; ops can dial quality without a code change).
 */
function envInt(value: string | undefined, fallback: number): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

/** Non-empty-string env override, else the fallback. */
function envStr(value: string | undefined, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

/**
 * High-quality capture constraints (005 — maximum video quality).
 *
 * We ask the camera for 1080p30 as the *ideal* target. `ideal` (not `exact`
 * or `min`) is deliberate: an unsatisfiable hard constraint makes
 * getUserMedia throw OverconstrainedError, which we classify as 'no-device'
 * and which would abort the join. With `ideal`, the browser picks the closest
 * mode it can — 1080p on a capable webcam, gracefully less on a weaker one —
 * and never rejects on resolution alone. The resolution the camera actually
 * delivers is the upper bound on the quality the SFU can forward, so we ask
 * for the most a typical webcam supports.
 *
 * Frame rate is held at 30: at a fixed bitrate budget, spending bits on
 * resolution rather than a higher frame rate yields a sharper, "higher
 * quality" image for talking-head video.
 *
 * Overridable via `VITE_MAX_VIDEO_{WIDTH,HEIGHT,FRAMERATE}` so the E2E suite
 * can capture at a low resolution (parallel software 1080p encodes would
 * otherwise saturate the test machine and make the suite flaky).
 */
export const HQ_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: envInt(import.meta.env.VITE_MAX_VIDEO_WIDTH, 1920) },
  height: { ideal: envInt(import.meta.env.VITE_MAX_VIDEO_HEIGHT, 1080) },
  frameRate: { ideal: envInt(import.meta.env.VITE_MAX_VIDEO_FRAMERATE, 30) },
}

/**
 * Video producer bitrate ceiling (bits/s) and initial ramp target (kbps),
 * also env-tunable (`VITE_MAX_VIDEO_BITRATE`). See RoomClient.produce().
 *
 * Default raised to 5 Mbps for 006 (VP9 SVC): with a single K-SVC encoding the
 * one ceiling is shared across all spatial layers, so the top (full-resolution)
 * layer needs extra headroom to keep ~1080p quality.
 */
export const VIDEO_MAX_BITRATE = envInt(import.meta.env.VITE_MAX_VIDEO_BITRATE, 5_000_000)
export const VIDEO_START_BITRATE = envInt(import.meta.env.VITE_VIDEO_START_BITRATE, 1000)

/**
 * VP9 SVC scalability mode (006 — adaptive per-receiver quality), env-tunable
 * via `VITE_VIDEO_SCALABILITY_MODE`. `L3T3_KEY` = 3 spatial × 3 temporal layers,
 * K-SVC (inter-layer prediction confined to key frames) so the SFU can switch a
 * receiver's spatial layer cleanly. Applied only when the browser can encode
 * VP9; otherwise the producer falls back to a single-layer encoding (no mode).
 */
export const VIDEO_SCALABILITY_MODE = envStr(
  import.meta.env.VITE_VIDEO_SCALABILITY_MODE,
  'L3T3_KEY',
)

/**
 * Lighter capture profile for mobile devices (008). Phones have no spare CPU /
 * battery / thermal headroom for 1080p; ~720p@30 at a modest bitrate looks good
 * on a phone-sized sender and stays sustainable (especially with hardware
 * H.264). Mobile values are independently env-tunable so ops can dial them
 * without a code change.
 */
export const MOBILE_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: envInt(import.meta.env.VITE_MOBILE_VIDEO_WIDTH, 1280) },
  height: { ideal: envInt(import.meta.env.VITE_MOBILE_VIDEO_HEIGHT, 720) },
  frameRate: { ideal: envInt(import.meta.env.VITE_MOBILE_VIDEO_FRAMERATE, 30) },
}

export const MOBILE_VIDEO_MAX_BITRATE = envInt(
  import.meta.env.VITE_MOBILE_VIDEO_BITRATE,
  1_500_000,
)

/** Capture constraints for the device class (008): mobile gets the lighter profile. */
export function videoCaptureConstraints(mobile: boolean): MediaTrackConstraints {
  return mobile ? MOBILE_VIDEO_CONSTRAINTS : HQ_VIDEO_CONSTRAINTS
}

/** Producer bitrate ceiling for the device class (008). */
export function videoMaxBitrate(mobile: boolean): number {
  return mobile ? MOBILE_VIDEO_MAX_BITRATE : VIDEO_MAX_BITRATE
}

export async function getLocalMedia({ video, audio }: MediaConstraints): Promise<MediaResult> {
  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, reason: 'unsupported' }
  }

  if (!video && !audio) {
    // Empty constraints — caller wants no media. Return an empty stream.
    return { ok: true, stream: new MediaStream() }
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video, audio })
    return { ok: true, stream }
  } catch (err) {
    return { ok: false, reason: classifyError(err) }
  }
}

function classifyError(err: unknown): MediaFailure {
  if (err instanceof Error) {
    switch (err.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return 'denied'
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'no-device'
    }
  }
  return 'unknown'
}

/** Stop every track on a stream — used in leave() to release devices. */
export function stopStream(stream: MediaStream | null): void {
  if (!stream) return
  for (const track of stream.getTracks()) {
    try {
      track.stop()
    } catch {
      // already stopped
    }
  }
}
